import type {
  CalendarResponse,
  CreateTaskBody,
  RecurrenceEditScope,
  TaskDTO,
  TaskType,
} from '@hearth/shared';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback, useState } from 'react';
import { useConfirm, useToast } from '../../components/ui';
import { api, apiUrl } from '../../lib/api';
import { DEFAULT_MEETING_REMINDER, browserTimeZone, entryNoun, mapReminder } from '../tasks/entryModel';
import { useRecurrenceScopePrompt } from '../tasks/recurrenceScope';
import { invalidateTaskQueries } from '../tasks/taskQueries';
import type { DraftSlot, Reschedule } from './model';

/** Every calendar range query; the prefix optimistic edits patch and roll back. */
export const CALENDAR_RANGE_KEY = ['calendar', 'range'] as const;

export type NewEntry = { type: TaskType; title: string; slot: DraftSlot };

/** "Meeting", "Note", "Task" — as the editor's toasts say it. */
function nounOf(type: TaskType): string {
  const noun = entryNoun(type);
  return noun.charAt(0).toUpperCase() + noun.slice(1);
}

export type CalendarActions = {
  /** Drag result: shown at once, then saved (asking which occurrences first); rolled back if refused. */
  reschedule: (task: TaskDTO, change: Reschedule) => Promise<void>;
  /** Quick create; resolves false when it failed (the error was shown). */
  create: (entry: NewEntry) => Promise<boolean>;
  setDone: (task: TaskDTO, done: boolean) => Promise<void>;
  /** Asks (occurrence scope or a confirm), deletes; resolves true once deleted. */
  remove: (task: TaskDTO) => Promise<boolean>;
  /** An action in flight, e.g. to disable buttons. */
  busy: boolean;
  /** The occurrence-scope dialog; render once. */
  element: ReactNode;
};

/** Calendar mutations with optimistic updates of the visible ranges and the toasts that report them. */
export function useCalendarActions(): CalendarActions {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const scopePrompt = useRecurrenceScopePrompt();
  const [pending, setPending] = useState(0);

  /** Applies `patch` to the entry in every cached range; returns the undo. */
  const patchCached = useCallback(
    (id: string, patch: Partial<TaskDTO>): (() => void) => {
      // Cancelling synchronously stops an in-flight fetch from overwriting the change with stale data.
      void queryClient.cancelQueries({ queryKey: CALENDAR_RANGE_KEY });
      const snapshots = queryClient.getQueriesData<CalendarResponse>({ queryKey: CALENDAR_RANGE_KEY });
      queryClient.setQueriesData<CalendarResponse>({ queryKey: CALENDAR_RANGE_KEY }, (data) =>
        data
          ? { ...data, items: data.items.map((item) => (item.id === id ? { ...item, ...patch } : item)) }
          : data,
      );
      return () => {
        for (const [key, data] of snapshots) queryClient.setQueryData(key, data);
      };
    },
    [queryClient],
  );

  const track = useCallback(async <T>(work: () => Promise<T>): Promise<T> => {
    setPending((n) => n + 1);
    try {
      return await work();
    } finally {
      setPending((n) => n - 1);
    }
  }, []);

  const { ask } = scopePrompt;

  const reschedule = useCallback(
    async (task: TaskDTO, change: Reschedule) => {
      const undo = patchCached(task.id, change);
      const scope: RecurrenceEditScope | null = task.seriesId ? await ask('edit') : 'this';
      if (!scope) {
        undo();
        return;
      }
      await track(async () => {
        try {
          await api.patch<TaskDTO>(`/api/tasks/${task.id}`, {
            ...change,
            timeZone: browserTimeZone(),
            scope,
          });
        } catch (error) {
          undo();
          toast.error(error);
        }
        await invalidateTaskQueries(queryClient, task.businessId);
      });
    },
    [ask, patchCached, queryClient, toast, track],
  );

  const create = useCallback(
    ({ type, title, slot }: NewEntry) =>
      track(async () => {
        const body: CreateTaskBody = {
          type,
          title,
          dueAt: slot.start.toISOString(),
          endAt: slot.end.toISOString(),
          allDay: slot.allDay,
          // The same default the full editor gives a new meeting.
          reminderMinutes:
            type !== 'meeting'
              ? null
              : slot.allDay
                ? mapReminder(DEFAULT_MEETING_REMINDER, true)
                : DEFAULT_MEETING_REMINDER,
          timeZone: browserTimeZone(),
        };
        try {
          await api.post<TaskDTO>('/api/tasks', body);
        } catch (error) {
          toast.error(error);
          return false;
        }
        toast.success(`${nounOf(type)} created`);
        await invalidateTaskQueries(queryClient);
        return true;
      }),
    [queryClient, toast, track],
  );

  const setDone = useCallback(
    (task: TaskDTO, done: boolean) =>
      track(async () => {
        const undo = patchCached(task.id, {
          status: done ? 'completed' : 'open',
          completedAt: done ? new Date().toISOString() : null,
        });
        try {
          await api.post(`/api/tasks/${task.id}/${done ? 'complete' : 'reopen'}`, done ? {} : undefined);
          toast.success(done ? 'Marked as done' : 'Reopened');
        } catch (error) {
          undo();
          toast.error(error);
        }
        await invalidateTaskQueries(queryClient, task.businessId);
      }),
    [patchCached, queryClient, toast, track],
  );

  const remove = useCallback(
    async (task: TaskDTO) => {
      let scope: RecurrenceEditScope = 'this';
      if (task.seriesId) {
        const chosen = await ask('delete');
        if (!chosen) return false;
        scope = chosen;
      } else if (
        !(await confirm({
          title: `Delete this ${entryNoun(task.type)}?`,
          message: `“${task.title}” will be removed.`,
          confirmLabel: 'Delete',
          danger: true,
        }))
      ) {
        return false;
      }
      return track(async () => {
        try {
          await api.delete(apiUrl(`/api/tasks/${task.id}`, { scope }));
        } catch (error) {
          toast.error(error);
          return false;
        }
        toast.success(`${nounOf(task.type)} deleted`);
        await invalidateTaskQueries(queryClient, task.businessId);
        return true;
      });
    },
    [ask, confirm, queryClient, toast, track],
  );

  return { reschedule, create, setDone, remove, busy: pending > 0, element: scopePrompt.element };
}
